import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { resetPassword } from '../services/api';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import toast from 'react-hot-toast';

const resetPasswordSchema = z.object({
  newPassword: z.string().min(8, 'Password must be at least 8 characters long'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const ResetPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isFirstTime = searchParams.get('isFirstTime') === 'true';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data) => {
    if (!token && !isFirstTime) {
      toast.error('Invalid or expired reset link. Please try again.');
      return navigate('/forgot-password');
    }

    setIsLoading(true);

    try {
      const response = await resetPassword(token, data.newPassword);
      const { data: responseData } = response;

      if (responseData.status) {
        toast.success(responseData.message || 'Password updated successfully');
        navigate('/login');
      } else {
        toast.error(responseData.message || 'Failed to update password');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>

      <div className="w-full max-w-md p-8 sm:p-10 space-y-8 glass rounded-2xl z-10 animate-slide-up m-4">
        <div className="text-center space-y-2">
          {isFirstTime ? (
            <>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Create New Password</h2>
              <p className="text-slate-500 text-sm">Welcome! Please set up your new password to continue.</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Reset Password</h2>
              <p className="text-slate-500 text-sm">Please enter your new password below.</p>
            </>
          )}
        </div>

        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>


          <Input
            label="New Password"
            id="newPassword"
            type="password"
            placeholder="••••••••"
            {...register('newPassword')}
            error={errors.newPassword?.message}
          />

          <Input
            label="Confirm New Password"
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            {...register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />

          <div className="pt-2">
            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isLoading}
            >
              {isFirstTime ? 'Set Password' : 'Reset Password'}
            </Button>
          </div>

          <button
            type="button"
            className="w-full text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors pt-2"
            onClick={() => navigate('/login')}
          >
            ← Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
